import React, { useState, useEffect } from 'react';
import api from '../lib/api';
import toast from 'react-hot-toast';
import { Wrench, Plus, Edit2, Trash2, X } from 'lucide-react';

const TiposServicoRota = () => {
  const [itens, setItens] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [formData, setFormData] = useState({ label: '', slug: '', ativo: true, ordem: 0 });

  useEffect(() => {
    load();
  }, []);

  const load = async () => {
    try {
      const res = await api.get('/tipos-servico-rota');
      setItens(Array.isArray(res.data) ? res.data : []);
    } catch {
      toast.error('Erro ao carregar tipos de serviço');
    } finally {
      setLoading(false);
    }
  };

  const openCreate = () => {
    setEditing(null);
    setFormData({ label: '', slug: '', ativo: true, ordem: (itens.length || 0) + 1 });
    setShowModal(true);
  };

  const openEdit = (item) => {
    setEditing(item);
    setFormData({
      label: item.label || '',
      slug: item.slug || '',
      ativo: item.ativo !== false,
      ordem: item.ordem || 0,
    });
    setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.label.trim()) {
      toast.error('Informe o nome');
      return;
    }
    try {
      if (editing) {
        await api.patch(`/tipos-servico-rota/${editing.id}`, formData);
        toast.success('Tipo atualizado!');
      } else {
        await api.post('/tipos-servico-rota', formData);
        toast.success('Tipo cadastrado!');
      }
      setShowModal(false);
      setEditing(null);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao salvar');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Desativar este tipo de serviço? Visitas antigas continuam com o valor anterior.')) return;
    try {
      await api.delete(`/tipos-servico-rota/${id}`);
      toast.success('Tipo desativado!');
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao desativar');
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <div className="w-12 h-12 border-4 border-emerald-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="tipos-servico-rota-page">
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-indigo-500 to-blue-600 flex items-center justify-center">
            <Wrench className="w-6 h-6 text-white" />
          </div>
          <div>
            <h1 className="text-3xl font-bold text-slate-900">Tipos de serviço (rotas)</h1>
            <p className="text-slate-600">Opções ao inserir cliente na rota (Preventiva, Corretiva, etc.)</p>
          </div>
        </div>
        <button
          type="button"
          onClick={openCreate}
          className="bg-gradient-to-r from-emerald-500 to-teal-600 text-white px-6 py-3 rounded-xl font-medium hover:shadow-lg transition-all flex items-center gap-2"
        >
          <Plus className="w-5 h-5" /> Novo tipo
        </button>
      </div>

      <div className="bg-white rounded-2xl shadow-sm overflow-hidden">
        <table className="w-full">
          <thead className="bg-slate-50 border-b border-slate-200">
            <tr>
              <th className="text-left py-4 px-6 text-sm font-medium text-slate-600">Ordem</th>
              <th className="text-left py-4 px-6 text-sm font-medium text-slate-600">Nome</th>
              <th className="text-left py-4 px-6 text-sm font-medium text-slate-600">Identificador</th>
              <th className="text-left py-4 px-6 text-sm font-medium text-slate-600">Status</th>
              <th className="text-right py-4 px-6 text-sm font-medium text-slate-600">Ações</th>
            </tr>
          </thead>
          <tbody>
            {itens.map((item) => (
              <tr key={item.id} className="border-b border-slate-100 hover:bg-slate-50 transition-colors">
                <td className="py-4 px-6 text-sm text-slate-600">{item.ordem}</td>
                <td className="py-4 px-6 text-sm font-medium text-slate-900">{item.label}</td>
                <td className="py-4 px-6 text-sm text-slate-500 font-mono">{item.slug}</td>
                <td className="py-4 px-6">
                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                    item.ativo !== false ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-500'
                  }`}>
                    {item.ativo !== false ? 'Ativo' : 'Inativo'}
                  </span>
                </td>
                <td className="py-4 px-6 text-right">
                  <div className="flex justify-end gap-1">
                    <button
                      type="button"
                      onClick={() => openEdit(item)}
                      className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg"
                      title="Editar"
                    >
                      <Edit2 className="w-4 h-4" />
                    </button>
                    {item.ativo !== false && (
                      <button
                        type="button"
                        onClick={() => handleDelete(item.id)}
                        className="p-2 text-red-600 hover:bg-red-50 rounded-lg"
                        title="Desativar"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {itens.length === 0 && (
          <p className="text-center text-slate-500 py-8">Nenhum tipo cadastrado</p>
        )}
      </div>

      {showModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-xl max-w-md w-full p-6">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-xl font-bold text-slate-900">
                {editing ? 'Editar tipo' : 'Novo tipo de serviço'}
              </h2>
              <button type="button" onClick={() => setShowModal(false)} className="p-2 hover:bg-slate-100 rounded-lg">
                <X className="w-5 h-5" />
              </button>
            </div>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Nome</label>
                <input
                  type="text"
                  value={formData.label}
                  onChange={(e) => setFormData({ ...formData, label: e.target.value })}
                  className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-200 outline-none"
                  placeholder="Ex: Preventiva, Visita Técnica"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Identificador (opcional)</label>
                <input
                  type="text"
                  value={formData.slug}
                  onChange={(e) => setFormData({
                    ...formData,
                    slug: e.target.value.toLowerCase().replace(/\s+/g, '_'),
                  })}
                  className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-200 outline-none font-mono text-sm"
                  placeholder="Gerado automaticamente se vazio"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Ordem</label>
                <input
                  type="number"
                  min={0}
                  value={formData.ordem}
                  onChange={(e) => setFormData({ ...formData, ordem: Number(e.target.value) || 0 })}
                  className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-200 outline-none"
                />
              </div>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={formData.ativo}
                  onChange={(e) => setFormData({ ...formData, ativo: e.target.checked })}
                  className="w-4 h-4 text-emerald-600 rounded"
                />
                <span className="text-sm text-slate-700">Ativo (aparece no dropdown das rotas)</span>
              </label>
              <div className="flex gap-3 pt-2">
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  className="flex-1 px-4 py-3 rounded-xl border border-slate-200 text-slate-600 font-medium hover:bg-slate-50"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  className="flex-1 px-4 py-3 rounded-xl bg-gradient-to-r from-emerald-500 to-teal-600 text-white font-medium hover:shadow-lg"
                >
                  {editing ? 'Salvar' : 'Cadastrar'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default TiposServicoRota;
